import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Check, UsersIcon } from 'lucide-react';
import { capitalize } from 'lodash';
import { useMemo } from 'react';

import { Table, TableBody, TableCell, TableRow, TableHeader, TableHead } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { setOrganizationFullAddress } from 'src/utils/escrow';
import { cn } from 'src/lib/utils';
import { AgreementFormEnum, EscrowPartyTypeEnum } from 'src/graphql/schema';

import { useParty } from '../model';

import SummaryCard from './SummaryCard';

type IRepresentativeRow = {
  name: string;
  email: string;
  phone: string;
  signatory: boolean;
};

const columnHelper = createColumnHelper<IRepresentativeRow>();
const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: (props) => <div className="text-sm">{props.getValue()}</div>,
  }),
  columnHelper.accessor('email', {
    header: 'Email',
    cell: (props) => <div className="text-sm">{props.getValue()}</div>,
  }),
  columnHelper.accessor('phone', {
    header: 'Phone number',
    cell: (props) => <div className="text-sm">{props.getValue() || '-'}</div>,
  }),
  columnHelper.accessor('signatory', {
    header: 'Signatory',
    cell: (props) => (props.getValue() ? <Check className="size-3.5 text-accent-software-escrow" /> : null),
  }),
];

const RepresentativesTable = () => {
  const { party, showSignatories } = useParty();

  const data = useMemo(
    () =>
      party?.contacts.map((contact) => ({
        name: contact.name,
        email: contact.email,
        phone: contact.phone || '-',
        signatory: contact.signatory,
      })) || [],
    [party],
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      columnVisibility: {
        signatory: showSignatories,
      },
    },
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-2">
      <div className="text-sm font-bold text-foreground">Representatives</div>
      <div className="rounded-lg border border-border grow overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              {table.getFlatHeaders().map((header) => (
                <TableHead key={header.id} className={cn(header.id === 'signatory' && 'w-24 text-center')}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const OrganizationDetailsTable = () => {
  const { party } = useParty();

  const address = {
    street: party?.street || '',
    streetNumber: party?.streetNumber || '',
    city: party?.city || '',
    postalCode: party?.postalCode || '',
    region: party?.region || '',
    country: party?.country || '',
  };

  const rows = [
    {
      id: 'organization-name',
      label: 'Organization name',
      value: party?.companyName,
    },
    {
      id: 'website',
      label: 'Website',
      value: party?.companyWebsite || '-',
    },
    {
      id: 'registration-number',
      label: 'Registration number',
      value: party?.companyRegistrationNumber || '-',
    },
    {
      id: 'address',
      label: 'Address',
      value: setOrganizationFullAddress(address),
    },
  ];

  return (
    <Table>
      <TableBody>
        {rows.map(({ id, label, value }) => (
          <TableRow key={id} className="border-0 hover:bg-white">
            <TableCell className="h-10 px-0 w-40 text-xs font-semibold text-muted-foreground py-2.5">{label}</TableCell>
            <TableCell data-separator className="h-10 w-5 px-0 pr-1 py-2.5 text-slate-400 text-right">
              :
            </TableCell>
            <TableCell className="h-10 py-2.5">
              <div className="min-w-47.5">{value}</div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const OrganizationCard = () => {
  const { type, agreementForm, onEdit } = useParty();

  return (
    <SummaryCard
      icon={UsersIcon}
      title={`${capitalize(type)} information`}
      description={
        agreementForm === AgreementFormEnum.Bipartite && type === EscrowPartyTypeEnum.Beneficiary
          ? 'Organization details and key points of contact.'
          : "Your organization's details, key points of contact, and signatories."
      }
      action={
        <Button variant="secondary" size="sm" onClick={onEdit}>
          Edit
        </Button>
      }
    >
      <div className="space-y-4">
        <OrganizationDetailsTable />
        <RepresentativesTable />
      </div>
    </SummaryCard>
  );
};

export default OrganizationCard;
